import { inEnum, isBoolean, isDefined, isNumber, isObject, isString } from "@ciaopeople/cp-shatsu/TypeChecking";
import { Experiment, getExperiment, getExperimentKV } from "@ciaopeople/cp-shatsu/ABTest";
import { HasAdCallParamsFilter } from "../ComposableResolver.js";
import { AdUnit } from "../../AdUnit.js";
import { AdCallParams } from "../../AdCallParams.js";
import { CP_AdvSiteName, CP_ContentValue, CP_DeviceTypeValue, CP_EmbedType, CP_InAppBrowserValue, CustomParams, getAdvSiteNameFromProduct, getFanpageValueFromExternalArea, getFanpageValueFromYMCategory } from "../CustomParams.js";
import { DetectedWebView, detectWebView } from "@ciaopeople/cp-shatsu/ClientDetection";
import { getCPProductFromHostname } from "../../../../config/CPProducts.js";
import { hasOwnProperty } from "@ciaopeople/cp-shatsu/Utils";
import { VideoInfoRawData } from "../../../../model/video/VideoInfo.js";
import { Yield } from "@ciaopeople/cp-shatsu/Ads";

/**
 * maybe useless - no evidence of its usage has been found
 */
interface FPAdData {
    pathname: string,
    hostname: string
}

function isFpAdData(a: any): a is FPAdData {
    return isObject(a) &&
        ("pathname" in a && isString(a.pathname) && a.pathname.trim() !== "") &&
        ("hostname" in a && isString(a.hostname) && a.hostname.trim() !== "");
}


interface HasFpAdData {
    _fpaddata: FPAdData
}

function hasFpAdData(a: any): a is HasFpAdData {
    return ("_fpaddata" in a && isFpAdData(a._fpaddata));
}




export interface ExternallyPassedContentAdData {
    embedType: CP_EmbedType,
    hot?: boolean,
    storyTitle?: string,
    refUrl?: string
    n_parag?: number,
    sitePage?: string,
    category?: string,
    area?: string,
    site?: CP_AdvSiteName,
}

export function isExternallyPassedContentAdData(obj: any): obj is ExternallyPassedContentAdData {
    return isObject(obj) &&
        hasOwnProperty(obj, 'embedType') && inEnum(obj['embedType'], CP_EmbedType) &&
        !hasOwnProperty(obj, 'hot') || isBoolean(obj['hot']) &&
        !hasOwnProperty(obj, 'storyTitle') || isString(obj['storyTitle']) &&
        !hasOwnProperty(obj, 'refUrl') || isString(obj['refUrl']) &&
        !hasOwnProperty(obj, 'n_parag') || isNumber(obj['n_parag']) &&
        !hasOwnProperty(obj, 'sitePage') || isString(obj['sitePage']) &&
        !hasOwnProperty(obj, 'category') || isString(obj['category']) &&
        !hasOwnProperty(obj, 'area') || isString(obj['area']) &&
        !hasOwnProperty(obj, 'site') || inEnum(obj['site'], CP_AdvSiteName);
}

const URL_EXTRACTOR = /([^\/?]+)(?:\/[a-z]\d+)?(?:\/+$|\/\?.*$|\?.*$|$)/i



function maxNChar(s: string, max: number): string {
    return s.substring(0, Math.min(s.length, max));
}


export class ContentSpecificAdData implements HasAdCallParamsFilter {


    private _site: CP_AdvSiteName = null;
    private hostname: string;
    private pathname: string;

    private inappBrowser: CP_InAppBrowserValue;
    private deviceType: CP_DeviceTypeValue;


    constructor(private window: Window,
        private extAdData: ExternallyPassedContentAdData,
        vwData: DetectedWebView
    ) {

        const detected = vwData ? vwData : detectWebView();

        if (extAdData && extAdData.refUrl) {
            const url = new URL(extAdData.refUrl);
            this.hostname = url.hostname;
            this.pathname = url.pathname;
        }
        else if (hasFpAdData(this.window)) {
            this.hostname = this.window._fpaddata.hostname;
            this.pathname = this.window._fpaddata.pathname;
        }
        else {
            this.hostname = this.window.location.hostname;
            this.pathname = this.window.location.pathname;
        }

        if (!detected.device?.other) {
            if (detected.native_app && detected.extra?.product) {

                switch (detected.extra.product) {
                    case "fanpage":
                        this.inappBrowser = CP_InAppBrowserValue.FANPAGE_APP;
                        break;
                    case "cookist_it":
                        this.inappBrowser = CP_InAppBrowserValue.COOKIST_IT_APP;
                        break;
                    case "cookist_com":
                        this.inappBrowser = CP_InAppBrowserValue.COOKIST_COM_APP;
                        break;
                    case "geopop":
                        this.inappBrowser = CP_InAppBrowserValue.GEOPOP_APP;
                        break;
                    case "kodami":
                        this.inappBrowser = CP_InAppBrowserValue.KODAMI_APP;
                        break;
                    case "wamily":
                        this.inappBrowser = CP_InAppBrowserValue.WAMILY_APP;
                        break;
                    case "lexplain":
                        this.inappBrowser = CP_InAppBrowserValue.LEXPLAIN_APP;
                        break;
                    default:
                        break;
                }
            }
            if (!this.inappBrowser) {
                if (detected.browser.webview) {
                    if (detected.platform.android) this.inappBrowser = CP_InAppBrowserValue.ANDROID_WEBVIEW;
                    else if (detected.platform.ios) this.inappBrowser = CP_InAppBrowserValue.SAFARI;
                }
            }
        }
        if (!this.inappBrowser) {
            this.inappBrowser = CP_InAppBrowserValue.NO;
        }

        if (detected.device?.phone) {
            this.deviceType = CP_DeviceTypeValue.MOBILE;
        }
        else if (detected.device?.tablet) {
            this.deviceType = CP_DeviceTypeValue.TABLET;
        }
        else this.deviceType = CP_DeviceTypeValue.DESKTOP;
    }

    filterAdCall(unit: AdUnit, info: VideoInfoRawData, params: AdCallParams): AdCallParams {

        const useParams: AdCallParams = params ? params : {};
        useParams.description_url = "https://" + this.hostname + this.pathname;
        const matches = this.pathname.match(URL_EXTRACTOR);
        const fpurl = maxNChar(matches && matches.length > 1 ? matches[1] : this.pathname, 40).replace(/\//g, '');



        let eType: CP_EmbedType = null;
        if (this.extAdData && this.extAdData.embedType) {
            eType = this.extAdData.embedType == CP_EmbedType.EMBED_GNEWS ? CP_EmbedType.EMBED_EXT : this.extAdData.embedType
        }

        const preBuild: CustomParams = {
            site: this.site,
            url: fpurl,
            inappbrowser: this.inappBrowser,
            device_category: this.deviceType,
        }


        useParams.custom_params = useParams.custom_params ? {
            ...useParams.custom_params,
            ...preBuild
        } : preBuild;

        if (eType) useParams.custom_params.type = eType;


        // START #13842 --- rinominato vecchio data-category in data-area, allineato data-category a quanto viene fatto in pagina per invio kv category=
        if (this.site == CP_AdvSiteName.FANPAGE || this.site == CP_AdvSiteName.KODAMI) { //#13904 aggiunto kv fanpage=kodami
            //vedi task #10793    
            if (this.extAdData?.area)
                useParams.custom_params.fanpage = getFanpageValueFromExternalArea(this.extAdData?.area)
            else
                useParams.custom_params.fanpage = getFanpageValueFromYMCategory(info.meta.category)
        }

        if (this.extAdData?.category)
            useParams.custom_params.category = this.extAdData.category.split("|").map(e => e.trim()).join(",")

        // --- END #13842 

        if (this.extAdData?.hot) useParams.custom_params.content = CP_ContentValue.HOT;
        if (info.meta?.channelName) useParams.custom_params.user = info.meta.channelName;
        if (isNumber(this.extAdData?.n_parag)) useParams.custom_params.paragraph_number = this.extAdData.n_parag;

        if (this.extAdData?.storyTitle && this.extAdData.storyTitle.trim().length > 0) {
            useParams.custom_params.story = maxNChar(this.extAdData.storyTitle.trim(), 40);
        }
        else if (info.meta?.storyTitle && info.meta.storyTitle.trim().length > 0) {
            useParams.custom_params.story = maxNChar(info.meta.storyTitle.trim(), 40);
        }
        if (this.extAdData?.sitePage) useParams.custom_params.page = this.extAdData.sitePage.trim();

        useParams.pre_serialized_custom_params = this.preSerializedCustomParamsBuilder(useParams)

        const experiments: Experiment[] = getExperimentKV().filter(e => isDefined(e) && isDefined(e.value));

        if (experiments.length > 0) {
            const exp: string = experiments.map(e => `${e.key}=${e.value}`).join("&")
            useParams.pre_serialized_custom_params.push(encodeURIComponent(exp));
        }

        return useParams;

    }


    private preSerializedCustomParamsBuilder(useParams: AdCallParams): string[] {

        const prebid_HY_KEY_REGEX = new RegExp("[?&]*pbj_high_yield=([^&]+)");
        const amz_HY_KEY_REGEX = new RegExp("[?&]*amz_high_yield=([^&]+)");

        const initial = useParams.pre_serialized_custom_params ? useParams.pre_serialized_custom_params : [];

        let pbjsHY: string | undefined = undefined;
        let amzHY: string | undefined = undefined;


        const cleaned = initial.map(e => {

            const decoded = decodeURIComponent(e)

            const matchesPbjs = decoded.match(prebid_HY_KEY_REGEX);
            const matchesAmz = decoded.match(amz_HY_KEY_REGEX);

            if (matchesPbjs && matchesPbjs.length == 2 && matchesPbjs[0].trim() !== "") {
                pbjsHY = matchesPbjs[1]
                return encodeURIComponent(decoded.replace(matchesPbjs[0], ""))
            } else if (matchesAmz && matchesAmz.length == 2 && matchesAmz[0].trim() !== "") {
                amzHY = matchesAmz[1]
                return encodeURIComponent(decoded.replace(matchesAmz[0], ""))
            }

            return e;
        })


        let highYield = "no";
        if (pbjsHY != undefined && amzHY != undefined) {
            highYield = Yield.highYieldChallenge(pbjsHY, amzHY, "video");
        } else if (pbjsHY != undefined && amzHY == undefined) {
            highYield = pbjsHY;
        } else if (pbjsHY == undefined && amzHY != undefined) {
            highYield = amzHY
        }

        cleaned.push(encodeURIComponent(`high_yield=${highYield}`))

        return cleaned

    }


    public get site(): CP_AdvSiteName {
        if (this._site == null) {
            this._site = this.extAdData?.site ? this.extAdData.site : getAdvSiteNameFromProduct(getCPProductFromHostname(this.hostname));
        }
        return this._site
    }

    public getRefHostname(): string {
        return this.hostname;
    }


    public getRefPathname(): string {
        return this.pathname;
    }


}